/**
 * ShapeIcon — simplified fixed-size shape wrapper for icons and small elements.
 * Morphs to a circle on hover by default.
 *
 * @example
 * <ShapeIcon shape="sunny" size={48}>
 *   <StarIcon />
 * </ShapeIcon>
 */

import type { CSSProperties } from "react";
import { ShapeMedia } from "./shape-media";
import type { ShapeIconProps } from "./types";

/**
 * ShapeIcon renders a fixed-size shape with icon content inside.
 */
export function ShapeIcon({
	shape,
	morphTo,
	size = 48,
	children,
	className,
	style,
	color,
	"aria-label": ariaLabel,
}: ShapeIconProps): React.JSX.Element {
	const computedStyle: CSSProperties = {
		width: size,
		height: size,
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
		backgroundColor: color,
		...style,
	};

	return (
		<ShapeMedia
			shape={shape}
			morphTo={morphTo}
			morphOn={morphTo ? "hover" : "none"}
			width={size}
			height={size}
			className={className}
			style={computedStyle}
			aria-label={ariaLabel}
		>
			{children}
		</ShapeMedia>
	);
}
